import { useMemo } from "react";

import { ColDef } from "ag-grid-community";
import i18next from "i18next";

import {
  BSCoreServicesDtoMarketplaceOrderDto,
  BSCoreServicesDtoMarketplaceOrderProductDto,
} from "api/reactQuery/querySchemas";
import { MarketplacePalletsTypes } from "components/Utils/enums";
import { COLUMN_TYPE_NAMES } from "constants/agGridConstants";
import {
  currencyFormatter,
  decimalNumberFormatter,
} from "helpers/agGrid/valueFormatters";

const useColDefs = () => {
  const productColumnDefs = useMemo<
    ColDef<BSCoreServicesDtoMarketplaceOrderProductDto>[]
  >(
    () => [
      { field: "productName", headerName: i18next.t("product_name") },
      {
        field: "productPrice",
        headerName: i18next.t("product_price"),
        valueFormatter: (params) => currencyFormatter(params, 4, 4),
      },
      {
        field: "productUnitsInPallet",
        headerName: i18next.t("product_units_in_pallet"),
      },
      {
        field: "pallet.type",
        filter: "agTextColumnFilter",
        filterParams: {
          textMatcher: ({ value, filterText }) => {
            const formattedValue = MarketplacePalletsTypes[value] || value;

            return formattedValue
              .toLowerCase()
              .includes(filterText.toLowerCase());
          },
        },
        headerName: i18next.t("pallet_type"),
        valueFormatter: (params) =>
          MarketplacePalletsTypes[params.value].toLowerCase() ||
          params.value.toLowerCase(),
      },
      { field: "productWeight", headerName: i18next.t("product_weight") },
      {
        field: "productVolumeInHL",
        headerName: i18next.t("product_volume_in_hl"),
      },
      {
        field: "orderedQuantitiesInPallets",
        headerName: i18next.t("ordered_quantities_in_pallets"),
      },
      { field: "orderedUnits", headerName: i18next.t("ordered_units") },
      {
        field: "totalBeerCostWithoutVat",
        headerName: i18next.t("total_beer_cost_without_vat_in_bgn"),
        valueFormatter: (params) => currencyFormatter(params, 2, 2),
      },
      {
        field: "totalPackagingCost",
        headerName: i18next.t("total_packaging_cost_in_bgn"),
        valueFormatter: (params) => currencyFormatter(params, 2, 2),
      },
      {
        field: "totalBeerAndPackagingCostWithVat",
        headerName: i18next.t("total_beer_and_packaging_cost_with_vat"),
        valueFormatter: (params) => currencyFormatter(params, 2, 2),
      },
      {
        field: "weightOfOrderUnit",
        headerName: i18next.t("weight_of_order_unit"),
      },
    ],
    []
  );
  const orderColumnDefs = useMemo<
    ColDef<BSCoreServicesDtoMarketplaceOrderDto>[]
  >(
    () => [
      { field: "id", headerName: i18next.t("id") },
      { field: "linkedToOrderId", headerName: i18next.t("linked_id") },
      { field: "distributorName", headerName: i18next.t("distributor_name") },
      { field: "depotName", headerName: i18next.t("depot_name") },
      {
        field: "dateCreated",
        headerName: i18next.t("date_created"),
        type: COLUMN_TYPE_NAMES.DATE_COLUMN,
      },
      {
        field: "deliveryDate",
        headerName: i18next.t("delivery_date"),
        type: COLUMN_TYPE_NAMES.DATE_COLUMN,
      },
      {
        field: "total.ttlVolumeInHL",
        headerName: i18next.t("ttl_volume_in_hl"),
        valueFormatter: decimalNumberFormatter,
      },
      {
        field: "total.ttlBeerCostWithoutVAT",
        headerName: i18next.t("ttl_beer_cost_wt_vat_in_bgn"),
        valueFormatter: currencyFormatter,
      },
      {
        field: "total.ttlBeerCostWithVAT",
        headerName: i18next.t("ttl_beer_cost_w_vat_in_bgn"),
        valueFormatter: currencyFormatter,
      },
      {
        field: "total.ttlPackagingCost",
        headerName: i18next.t("ttl_packaging_cost_in_bgn"),
        valueFormatter: (params) => currencyFormatter(params, 2, 2),
      },
      {
        field: "total.ttlBeerWithVATAndPackagingCost",
        headerName: i18next.t("ttl_beer_w_vat_packaging_cost_in_bgn"),
        valueFormatter: (params) => currencyFormatter(params, 2, 2),
      },
    ],
    []
  );

  return { orderColumnDefs, productColumnDefs };
};

export default useColDefs;
